import { useSyncExternalStore } from "react";

export const PORTAL_IMAGE_ROLLOUT_CONTRACT_VERSION = "portal-image-rollout-v2";

const STATIC_ROLLOUT_ENV = Object.freeze({
  enabled: process.env.REACT_APP_OPTIMIZED_IMAGES_ENABLED,
  forceLegacy: process.env.REACT_APP_OPTIMIZED_IMAGES_FORCE_LEGACY,
  percentage: process.env.REACT_APP_OPTIMIZED_IMAGES_ROLLOUT_PERCENT,
  stage: process.env.REACT_APP_OPTIMIZED_IMAGES_ROLLOUT_STAGE,
  telemetryEnabled: process.env.REACT_APP_OPTIMIZED_IMAGES_TELEMETRY_ENABLED,
  legacyFallbackMode: process.env.REACT_APP_OPTIMIZED_IMAGES_LEGACY_FALLBACK_MODE,
  legacyFallbackRemovalPercentage:
    process.env.REACT_APP_OPTIMIZED_IMAGES_LEGACY_FALLBACK_REMOVAL_PERCENT,
});

export const DEFAULT_PORTAL_IMAGE_ROLLOUT = Object.freeze({
  contractVersion: PORTAL_IMAGE_ROLLOUT_CONTRACT_VERSION,
  enabled: true,
  forceLegacy: false,
  percentage: 100,
  stage: "observe",
  telemetryEnabled: true,
  seed: "portal-image-v2",
  legacyFallbackMode: "enabled",
  legacyFallbackRemovalPercentage: 0,
  revision: 0,
});

let runtimeRollout = {};
let runtimeRevision = 0;
let hookSnapshotRevision = -1;
let hookConfigSnapshot = null;
const runtimeListeners = new Set();

const normalizeBoolean = (value, fallback) => {
  if (value === undefined || value === null || value === "") return Boolean(fallback);
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return Boolean(value);
};

const normalizePercentage = (value, fallback = 100) => {
  if (value === undefined || value === null || value === "") return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(100, Math.max(0, Math.round(numeric)));
};

const readRuntimeRollout = () => {
  const windowValue = typeof window === "undefined" ? null : window.__ECOTOUR_IMAGE_ROLLOUT__;
  return {
    ...(windowValue && typeof windowValue === "object" && !Array.isArray(windowValue)
      ? windowValue
      : {}),
    ...runtimeRollout,
  };
};

const normalizeFallbackMode = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["enabled", "canary", "disabled"].includes(normalized) ? normalized : "enabled";
};

export const normalizePortalImageRolloutConfig = (value = {}) => {
  const stage = String(value.stage || DEFAULT_PORTAL_IMAGE_ROLLOUT.stage).trim().toLowerCase()
    || DEFAULT_PORTAL_IMAGE_ROLLOUT.stage;
  let forceLegacy = normalizeBoolean(value.forceLegacy, DEFAULT_PORTAL_IMAGE_ROLLOUT.forceLegacy);
  let legacyFallbackMode = normalizeFallbackMode(
    value.legacyFallbackMode || DEFAULT_PORTAL_IMAGE_ROLLOUT.legacyFallbackMode,
  );
  let legacyFallbackRemovalPercentage = normalizePercentage(
    value.legacyFallbackRemovalPercentage,
    DEFAULT_PORTAL_IMAGE_ROLLOUT.legacyFallbackRemovalPercentage,
  );

  // Stage semantics are a safety boundary. Observe is canonical-first with a
  // complete legacy fallback, and canary removal is always bounded. This also
  // protects the portal while backend and frontend releases overlap.
  if (stage === "observe") {
    legacyFallbackMode = "enabled";
    legacyFallbackRemovalPercentage = 0;
  } else if (stage === "canary") {
    if (legacyFallbackMode !== "enabled") legacyFallbackMode = "canary";
    legacyFallbackRemovalPercentage = legacyFallbackMode === "enabled"
      ? 0
      : Math.min(50, legacyFallbackRemovalPercentage);
  } else if (stage === "rollback") {
    forceLegacy = true;
    legacyFallbackMode = "enabled";
    legacyFallbackRemovalPercentage = 0;
  }

  return {
    contractVersion: PORTAL_IMAGE_ROLLOUT_CONTRACT_VERSION,
    enabled: normalizeBoolean(value.enabled, DEFAULT_PORTAL_IMAGE_ROLLOUT.enabled),
    forceLegacy,
    percentage: normalizePercentage(value.percentage, DEFAULT_PORTAL_IMAGE_ROLLOUT.percentage),
    stage,
    telemetryEnabled: normalizeBoolean(
      value.telemetryEnabled,
      DEFAULT_PORTAL_IMAGE_ROLLOUT.telemetryEnabled,
    ),
    seed: String(value.seed || DEFAULT_PORTAL_IMAGE_ROLLOUT.seed).trim() || DEFAULT_PORTAL_IMAGE_ROLLOUT.seed,
    legacyFallbackMode,
    legacyFallbackRemovalPercentage,
    revision: Math.max(0, Number.parseInt(value.revision, 10) || 0),
  };
};

export const setRuntimePortalImageRolloutConfig = (value = {}) => {
  runtimeRollout = normalizePortalImageRolloutConfig(value);
  runtimeRevision += 1;
  runtimeListeners.forEach((listener) => listener());
  return runtimeRollout;
};

export const subscribePortalImageRollout = (listener) => {
  runtimeListeners.add(listener);
  return () => runtimeListeners.delete(listener);
};

const getRuntimeRevision = () => runtimeRevision;

const getHookConfigSnapshot = () => {
  if (hookConfigSnapshot === null || hookSnapshotRevision !== runtimeRevision) {
    hookConfigSnapshot = getPortalImageRolloutConfig();
    hookSnapshotRevision = runtimeRevision;
  }
  return hookConfigSnapshot;
};

export const usePortalImageRolloutConfig = () => {
  useSyncExternalStore(
    subscribePortalImageRollout,
    getRuntimeRevision,
    getRuntimeRevision,
  );
  // Configuration identity is part of SmartImage's source-plan lifecycle.
  // Rebuilding an equivalent object on every render makes the source chain
  // appear changed and repeatedly cancels newly granted image work.
  return getHookConfigSnapshot();
};

export const __resetPortalImageRolloutForTests = () => {
  runtimeRollout = {};
  runtimeRevision += 1;
  runtimeListeners.forEach((listener) => listener());
};

export const getPortalImageRolloutConfig = (override = null) => {
  const runtime = readRuntimeRollout();
  const normalized = normalizePortalImageRolloutConfig({
    enabled: STATIC_ROLLOUT_ENV.enabled,
    forceLegacy: STATIC_ROLLOUT_ENV.forceLegacy,
    percentage: STATIC_ROLLOUT_ENV.percentage,
    stage: STATIC_ROLLOUT_ENV.stage,
    telemetryEnabled: STATIC_ROLLOUT_ENV.telemetryEnabled,
    legacyFallbackMode: STATIC_ROLLOUT_ENV.legacyFallbackMode,
    legacyFallbackRemovalPercentage: STATIC_ROLLOUT_ENV.legacyFallbackRemovalPercentage,
    ...runtime,
    ...(override && typeof override === "object" ? override : {}),
  });
  if (normalized.forceLegacy) {
    return {
      ...normalized,
      legacyFallbackMode: "enabled",
      legacyFallbackRemovalPercentage: 0,
    };
  }
  return normalized;
};

export const hashPortalImageRolloutKey = (value = "") => {
  const input = String(value || "");
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const resolvePortalImageRollout = ({ key = "", surfaceKey = "", config = null } = {}) => {
  const rollout = getPortalImageRolloutConfig(config);
  const rolloutKey = String(key || surfaceKey || "unidentified-image");
  const bucket = hashPortalImageRolloutKey(`${rollout.seed}:${rolloutKey}`) % 100;
  const fallbackRemovalBucket = hashPortalImageRolloutKey(
    `${rollout.seed}:legacy-fallback:${rolloutKey}`,
  ) % 100;
  let optimizedEnabled = true;
  let reason = "included";

  if (!rollout.enabled) {
    optimizedEnabled = false;
    reason = "disabled";
  } else if (rollout.forceLegacy) {
    optimizedEnabled = false;
    reason = "force-legacy";
  } else if (rollout.percentage <= 0) {
    optimizedEnabled = false;
    reason = "percentage-excluded";
  } else if (rollout.percentage < 100 && bucket >= rollout.percentage) {
    optimizedEnabled = false;
    reason = "percentage-excluded";
  }

  let legacyFallbackAllowed = true;
  let fallbackDecisionReason = "enabled";
  if (!optimizedEnabled) {
    fallbackDecisionReason = "optimized-disabled";
  } else if (rollout.legacyFallbackMode === "disabled") {
    legacyFallbackAllowed = false;
    fallbackDecisionReason = "disabled";
  } else if (
    rollout.legacyFallbackMode === "canary" &&
    rollout.legacyFallbackRemovalPercentage > 0 &&
    fallbackRemovalBucket < rollout.legacyFallbackRemovalPercentage
  ) {
    legacyFallbackAllowed = false;
    fallbackDecisionReason = "canary-disabled";
  } else if (rollout.legacyFallbackMode === "canary") {
    fallbackDecisionReason = "canary-retained";
  }

  return Object.freeze({
    ...rollout,
    key: rolloutKey,
    bucket,
    fallbackRemovalBucket,
    optimizedEnabled,
    reason,
    legacyFallbackAllowed,
    fallbackDecisionReason,
  });
};

export default resolvePortalImageRollout;
